import { PartialVLF } from 'components/Search/types';
import { buildSavedSearchParams } from 'components/Search/functions';

/**
 * Compare current filters with last applied saved search filters
 * to determine if quick filter selection should be cleared
 * @param currentFilters - Current filter state
 * @param lastQuickFilterRef - Reference to last applied quick filter
 * @returns true if filters have diverged and quick filter should be cleared
 */
export const shouldClearQuickFilter = (
  currentFilters: PartialVLF,
  lastQuickFilterRef: React.MutableRefObject<any>,
): boolean => {
  if (!lastQuickFilterRef.current) {
    return false;
  }

  // Get the saved search payload that was applied
  const lastPayload = lastQuickFilterRef.current?.payload || {};
  const lastParamsString = buildSavedSearchParams({ payload: lastPayload });
  const lastParams = new URLSearchParams(lastParamsString);

  // Convert current filters to URLSearchParams for comparison
  const currentParams = new URLSearchParams();
  Object.entries(currentFilters).forEach(([key, value]) => {
    if (
      value !== null &&
      value !== undefined &&
      value !== '' &&
      key !== 'sort' &&
      key !== 'dir'
    ) {
      if (Array.isArray(value)) {
        // Join array with comma to match buildSavedSearchParams format
        if (value.length > 0) {
          currentParams.set(key, value.join(','));
        }
      } else {
        currentParams.set(key, String(value));
      }
    }
  });

  // Compare param strings (sort them for consistent comparison)
  const lastParamsSorted = Array.from(lastParams.entries())
    .filter(([key]) => key !== 'sort' && key !== 'dir')
    .sort()
    .map(([k, v]) => `${k}=${v}`)
    .join('&');

  const currentParamsSorted = Array.from(currentParams.entries())
    .sort()
    .map(([k, v]) => `${k}=${v}`)
    .join('&');

  // Return true if filters don't match (should clear quick filter)
  return lastParamsSorted !== currentParamsSorted;
};
